// CB:BEGIN chat-panel
/**
 * ChatPanel — project-scoped messaging for homeowners, contractors, and admins.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import type { ChatMessage, ChatPanelProps, QATemplate } from './types';

const POLL_INTERVAL_MS = 12000;

const ROLE_FALLBACK: Record<string, string> = {
  HOMEOWNER:  'Homeowner',
  CONTRACTOR: 'Contractor',
  ADMIN:      'CoreBids Support',
};

function formatTime(iso: string) {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
}
function formatDate(iso: string) {
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return ''; }
}
function isSystemMsg(m: ChatMessage) {
  return m.messageType !== 'user_message' && m.messageType !== 'template_question' && m.messageType !== 'template_response';
}

export default function ChatPanel({
  projectId,
  currentUserId,
  currentUserRole,
  projectStatus,
  isPostSelection = false,
  className = '',
}: ChatPanelProps) {
  const { getToken } = useAuth();
  const [messages, setMessages]     = useState<ChatMessage[]>([]);
  const [threadId, setThreadId]     = useState<string | null>(null);
  const [unreadCount, setUnread]    = useState(0);
  const [templates, setTemplates]   = useState<QATemplate[]>([]);
  const [isPreSelection, setIsPre]  = useState(false);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [expanded, setExpanded]     = useState(false);
  const [body, setBody]             = useState('');
  const [sending, setSending]       = useState(false);
  const [helpSent, setHelpSent]     = useState(false);
  const [sendingHelp, setSendingHelp] = useState(false);
  const pollRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const apiFetch = useCallback(async (path: string, opts: RequestInit = {}) => {
    let authHeader: Record<string, string> = {};
    try {
      const token = await getToken();
      if (token) authHeader = { Authorization: `Bearer ${token}` };
    } catch {}
    if (!authHeader.Authorization) {
      try { const devAuth = localStorage.getItem('devAuth'); if (devAuth) { const { token } = JSON.parse(devAuth); if (token) authHeader = { Authorization: `Bearer ${token}` }; } } catch {}
    }
    // CB:BEGIN impersonation-header-chat
    let impersonateHeader: Record<string, string> = {};
    try {
      const raw = sessionStorage.getItem('cb_impersonation');
      if (raw) {
        const parsed = JSON.parse(raw);
        const TTL_MS = 30 * 60 * 1000;
        if (parsed?.token && parsed?.startedAt && Date.now() - parsed.startedAt < TTL_MS) {
          impersonateHeader = { 'X-Impersonate-Token': parsed.token };
        }
      }
    } catch {}
    // CB:END impersonation-header-chat
    return fetch(path, { ...opts, headers: { 'Content-Type': 'application/json', ...authHeader, ...impersonateHeader, ...(opts.headers as Record<string, string> ?? {}) } });
  }, [getToken]);

  const loadMessages = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/projects/${projectId}/messages`);
      if (!res.ok) { if (res.status === 403) { setError('No access.'); return; } throw new Error(`HTTP ${res.status}`); }
      const data = await res.json();
      setMessages(data.messages ?? []);
      setThreadId(data.threadId ?? null);
      setUnread(data.unreadCount ?? 0);
      setError(null);
    } catch { setError('Failed to load messages.'); }
    finally { setLoading(false); }
  }, [projectId]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/projects/${projectId}/messages/templates`);
      if (!res.ok) return;
      const data = await res.json();
      setTemplates(data.templates ?? []);
      setIsPre(data.isPreSelection ?? false);
    } catch {}
  }, [projectId]);

  const markRead = useCallback(async () => {
    if (!threadId) return;
    try { await apiFetch(`/api/projects/${projectId}/messages/read`, { method: 'POST', body: JSON.stringify({}) }); setUnread(0); } catch {}
  }, [projectId, threadId]);

  useEffect(() => {
    loadMessages(); loadTemplates();
    pollRef.current = setInterval(loadMessages, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadMessages, loadTemplates]);

  useEffect(() => { if (expanded && unreadCount > 0) markRead(); }, [expanded, unreadCount, markRead]);
  useEffect(() => { if (expanded) bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length, expanded]);

  const sendMessage = async (msgBody: string, templateKey?: string) => {
    try {
      const res = await apiFetch(`/api/projects/${projectId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body: msgBody, ...(templateKey ? { templateKey } : {}) }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error ?? `HTTP ${res.status}`); }
      await loadMessages();
    } catch (e: any) { alert(e.message ?? 'Failed to send.'); }
  };

  const handleSend = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try { await sendMessage(text); setBody(''); textareaRef.current?.focus(); } finally { setSending(false); }
  };

  const handleRequestHelp = async () => {
    if (sendingHelp || helpSent) return;
    setSendingHelp(true);
    try {
      const res = await apiFetch(`/api/projects/${projectId}/messages/request-help`, { method: 'POST' });
      if (res.ok) {
        setHelpSent(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error ?? 'Failed to send help request. Please try again.');
      }
    } catch { alert('Failed to send help request. Please check your connection.'); }
    finally { setSendingHelp(false); }
  };

  const isAdminView = currentUserRole === 'ADMIN';
  const isRestrictedContractor = !isPostSelection && currentUserRole === 'CONTRACTOR' && isPreSelection;

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }} className={className}>
      {/* Header — uses div+role to avoid nested button issues */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: expanded ? '1px solid #e5e7eb' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(v => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(v => !v); }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Messages</span>
          {isAdminView && (
            <span style={{ fontSize: 11, fontWeight: 600, background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 12 }}>Admin</span>
          )}
          {isPreSelection && !isAdminView && (
            <span style={{ fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 12 }}>Pre-selection</span>
          )}
          {unreadCount > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, background: '#ef4444', color: 'white', padding: '2px 7px', borderRadius: 12, minWidth: 20, textAlign: 'center' }}>
              {unreadCount}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isAdminView && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRequestHelp(); }}
              disabled={sendingHelp || helpSent}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 10px',
                borderRadius: 12,
                border: '1px solid #d1d5db',
                background: helpSent ? '#d1fae5' : 'white',
                color: helpSent ? '#065f46' : '#374151',
                cursor: sendingHelp || helpSent ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {helpSent ? 'Help requested' : sendingHelp ? '…' : 'Request help'}
            </button>
          )}
          <svg
            style={{ width: 16, height: 16, color: '#9ca3af', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {/* Help confirmation banner */}
      {helpSent && (
        <div style={{ background: '#d1fae5', borderBottom: '1px solid #a7f3d0', padding: '8px 20px', fontSize: 13, color: '#065f46', fontWeight: 500 }}>
          Your request has been sent to the CoreBids team. We'll reach out shortly.
        </div>
      )}

      {/* Body */}
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', height: 380 }}>
          {error ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#ef4444', padding: 16 }}>
              {error}
            </div>
          ) : loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#9ca3af', padding: 16 }}>
              Loading messages…
            </div>
          ) : (
            <>
              {/* Message list */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {messages.length === 0 ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#9ca3af' }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>No messages yet</p>
                    <p style={{ fontSize: 13 }}>Send a message to start the conversation.</p>
                  </div>
                ) : (() => {
                  let lastDate = '';
                  return messages.map(m => {
                    const dateLabel = formatDate(m.sentAt);
                    const showDate  = dateLabel !== lastDate;
                    lastDate = dateLabel;

                    // System message
                    if (isSystemMsg(m)) {
                      return (
                        <React.Fragment key={m.id}>
                          {showDate && <DateDivider label={dateLabel} />}
                          <div style={{ textAlign: 'center', padding: '4px 0' }}>
                            <span style={{ fontSize: 12, color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: 12 }}>
                              {m.body}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    }

                    const isOwn = m.senderUserId === currentUserId;
                    const label = isOwn ? 'You' : (m.senderName ?? ROLE_FALLBACK[m.senderRole] ?? m.senderRole);

                    return (
                      <React.Fragment key={m.id}>
                        {showDate && <DateDivider label={dateLabel} />}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: isOwn ? 'flex-end' : 'flex-start' }}>
                          <span style={{ fontSize: 11, color: '#9ca3af', paddingLeft: 4, paddingRight: 4 }}>{label}</span>
                          <div style={{
                            maxWidth: '72%',
                            padding: '9px 13px',
                            borderRadius: isOwn ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                            fontSize: 14,
                            lineHeight: 1.45,
                            wordBreak: 'break-word',
                            background: isOwn ? '#2563eb' : '#f3f4f6',
                            color: isOwn ? '#fff' : '#111827',
                          }}>
                            {m.body}
                          </div>
                          <span style={{ fontSize: 11, color: '#9ca3af', paddingLeft: 4, paddingRight: 4 }}>{formatTime(m.sentAt)}</span>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
                <div ref={bottomRef} />
              </div>

              {/* Admin notice */}
              {isAdminView && (
                <div style={{ padding: '8px 20px', background: '#f5f3ff', borderTop: '1px solid #ede9fe', fontSize: 12, color: '#6d28d9' }}>
                  You are viewing this thread as a CoreBids support observer.
                </div>
              )}

              {/* Pre-selection restriction */}
              {isRestrictedContractor && (
                <div style={{ padding: '8px 20px', background: '#fffbeb', borderTop: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
                  Limited messaging before contractor selection.
                </div>
              )}

              {/* Q&A templates */}
              {isPreSelection && currentUserRole === 'HOMEOWNER' && templates.length > 0 && (
                <div style={{ padding: '8px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {templates.map(tpl => (
                    <button
                      key={tpl.key}
                      type="button"
                      onClick={() => sendMessage(tpl.body, tpl.key)}
                      style={{ fontSize: 12, padding: '4px 10px', borderRadius: 16, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', color: '#374151' }}
                    >
                      {tpl.label ?? tpl.key}
                    </button>
                  ))}
                </div>
              )}

              {/* Composer */}
              {!isAdminView && (
                <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid #e5e7eb', background: 'white' }}>
                  <textarea
                    ref={textareaRef}
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder={isRestrictedContractor ? 'Reply to homeowner questions…' : 'Type a message…'}
                    disabled={sending}
                    rows={2}
                    style={{
                      flex: 1,
                      resize: 'none',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontSize: 14,
                      fontFamily: 'inherit',
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={!body.trim() || sending}
                    style={{
                      alignSelf: 'flex-end',
                      padding: '9px 18px',
                      borderRadius: 8,
                      border: 'none',
                      background: body.trim() && !sending ? '#2563eb' : '#d1d5db',
                      color: 'white',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: body.trim() && !sending ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sending ? '…' : 'Send'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DateDivider({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
      <div style={{ flex: 1, borderTop: '1px solid #e5e7eb' }} />
      <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>
      <div style={{ flex: 1, borderTop: '1px solid #e5e7eb' }} />
    </div>
  );
}
// CB:END chat-panel
